import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";

interface ProfileData {
  name: string;
  role: string;
  email: string;
  phone: string;
  employeeId: string;
  department: string;
  joinDate: string;
  certifications: string[];
}

interface ProfileEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  profileData: ProfileData;
  onSave: (data: ProfileData) => void;
}

export default function ProfileEditModal({
  isOpen,
  onClose,
  profileData,
  onSave,
}: ProfileEditModalProps) {
  const [editForm, setEditForm] = useState({ ...profileData });

  const handleSave = () => {
    onSave(editForm);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[100] p-5"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            className="bg-white rounded-3xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
            style={{
              boxShadow:
                "0 20px 60px rgba(0, 0, 0, 0.3), 0 0 100px rgba(27, 42, 91, 0.1)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <h2
                className="text-[#0A0F1C]"
                style={{
                  fontSize: "24px",
                  letterSpacing: "-0.02em",
                  fontWeight: "700",
                }}
              >
                Edit Profile
              </h2>
              <motion.button
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-[#F9FAFB] flex items-center justify-center"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path
                    d="M5 5L15 15M5 15L15 5"
                    stroke="#0A0F1C"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </motion.button>
            </div>

            {/* Form */}
            <div className="space-y-5">
              <div>
                <label
                  className="text-[#717182] block mb-2"
                  style={{
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  NAME
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] border-2 border-transparent focus:border-[#1B2A5B] outline-none transition-colors"
                  style={{ fontSize: "15px", fontWeight: "500" }}
                  value={editForm.name}
                  onChange={(e) =>
                    setEditForm({ ...editForm, name: e.target.value })
                  }
                />
              </div>

              <div>
                <label
                  className="text-[#717182] block mb-2"
                  style={{
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  ROLE
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] border-2 border-transparent focus:border-[#1B2A5B] outline-none transition-colors"
                  style={{ fontSize: "15px", fontWeight: "500" }}
                  value={editForm.role}
                  onChange={(e) =>
                    setEditForm({ ...editForm, role: e.target.value })
                  }
                />
              </div>

              <div>
                <label
                  className="text-[#717182] block mb-2"
                  style={{
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  EMAIL
                </label>
                <input
                  type="email"
                  className="w-full px-4 py-3 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] border-2 border-transparent focus:border-[#1B2A5B] outline-none transition-colors"
                  style={{ fontSize: "15px", fontWeight: "500" }}
                  value={editForm.email}
                  onChange={(e) =>
                    setEditForm({ ...editForm, email: e.target.value })
                  }
                />
              </div>

              <div>
                <label
                  className="text-[#717182] block mb-2"
                  style={{
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  PHONE
                </label>
                <input
                  type="tel"
                  className="w-full px-4 py-3 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] border-2 border-transparent focus:border-[#1B2A5B] outline-none transition-colors"
                  style={{ fontSize: "15px", fontWeight: "500" }}
                  value={editForm.phone}
                  onChange={(e) =>
                    setEditForm({ ...editForm, phone: e.target.value })
                  }
                />
              </div>

              <div>
                <label
                  className="text-[#717182] block mb-2"
                  style={{
                    fontSize: "11px",
                    letterSpacing: "0.05em",
                    fontWeight: "600",
                  }}
                >
                  DEPARTMENT
                </label>
                <input
                  type="text"
                  className="w-full px-4 py-3 rounded-xl bg-[#F9FAFB] text-[#0A0F1C] border-2 border-transparent focus:border-[#1B2A5B] outline-none transition-colors"
                  style={{ fontSize: "15px", fontWeight: "500" }}
                  value={editForm.department}
                  onChange={(e) =>
                    setEditForm({ ...editForm, department: e.target.value })
                  }
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 mt-8">
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={onClose}
                className="flex-1 py-3 px-4 rounded-xl border-2 border-[#E5E7EB] text-[#717182]"
                style={{ fontSize: "15px", fontWeight: "700" }}
              >
                Cancel
              </motion.button>
              <motion.button
                whileTap={{ scale: 0.98 }}
                onClick={handleSave}
                className="flex-1 py-3 px-4 rounded-xl text-white"
                style={{
                  background:
                    "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                  fontSize: "15px",
                  fontWeight: "700",
                  boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                }}
              >
                Save Changes
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}